import type {Flag} from '@sail/access';
import type {ConnectElementConfigEnabledKey} from '../../connect/ConnectJSInterface/ConnectElementList';

export const ComponentOverrideFlag: Partial<
  Record<ConnectElementConfigEnabledKey, Flag>
> = {
  account_management: 'enable_account_management_component' as const,
  account_onboarding: 'enable_account_onboarding_component' as const,
  app_onboarding: 'enable_app_onboarding_component' as const,
  app_install: 'enable_app_install_component' as const,
  app_viewport: 'enable_app_viewport_component' as const,
  balances: 'enable_balances_component' as const,
  balance_report: 'enable_balance_report_component' as const,
  business_profile_management:
    'enable_business_profile_management_component' as const,
  capital_financing: 'enable_capital_financing_component' as const,
  capital_financing_application:
    'enable_capital_financing_application_component' as const,
  capital_financing_manual_payment:
    'enable_capital_financing_manual_payment_component' as const,
  capital_financing_promotion:
    'enable_capital_financing_promotion_component' as const,
  capital_overview: 'enable_capital_overview_component' as const,
  payment_disputes: 'enable_payment_disputes_component' as const,
  disputes_list: 'enable_disputes_list_component' as const,
  documents: 'enable_documents_component' as const,
  instant_payouts: 'enable_instant_payouts_component' as const,
  instant_payouts_promotion:
    'enable_instant_payouts_promotion_component' as const,
  issuing_card: 'enable_issuing_card_component' as const,
  issuing_cards_list: 'enable_issuing_cards_list_component' as const,
  issuing_cardholder_create:
    'enable_issuing_cardholder_create_component' as const,
  notification_banner: 'enable_notification_banner_component' as const,
  payment_details: 'enable_payment_details_component' as const,
  payments: 'enable_payments_component' as const,
  payouts: 'enable_payouts_component' as const,
  payouts_list: 'enable_payouts_list_component' as const,
  payout_details: 'enable_payout_details_component' as const,
  product_tax_code_selector:
    'enable_product_tax_code_selector_component' as const,
  tax_registrations: 'enable_tax_registrations_component' as const,
  tax_settings: 'enable_tax_settings_component' as const,
  tax_threshold_monitoring:
    'enable_tax_threshold_monitoring_component' as const,
  export_tax_transactions: 'enable_export_tax_transactions_component' as const,
  transactions_list: 'enable_transactions_list_component' as const,
  financial_account: 'enable_financial_account_component' as const,
  financial_account_transactions:
    'enable_financial_account_transactions_component' as const,
  financial_accounts: 'enable_financial_accounts_component' as const,
  financial_accounts_transactions:
    'enable_financial_accounts_transactions_component' as const,
  financial_account_rewards:
    'enable_financial_account_rewards_component' as const,
  recipients_list: 'enable_recipients_list_component' as const,
  // TODO(@stevekaliski): Deprecate
  treasury_financial_account:
    'enable_treasury_financial_account_component' as const,
  recipients: 'enable_recipients_component' as const,
  reporting_chart: 'enable_reporting_chart_component' as const,
  payout_reconciliation_report:
    'enable_payout_reconciliation_report_component' as const,
  terminal_hardware_orders:
    'enable_terminal_hardware_orders_component' as const,
  terminal_hardware_shop: 'enable_terminal_hardware_shop_component' as const,
  check_scanning: 'enable_check_scanning_component' as const,
  bills: 'enable_bills_component' as const,
  agentic_commerce_settings:
    'enable_agentic_commerce_settings_component' as const,
  network_cost_passthrough_report:
    'enable_network_cost_passthrough_report_component' as const,
};

// These are client-side flags received from changing the URL query params like so ?show_ark_debug_tools=true
// Flags in this list cannot be updated via go/flags and are meant only for testing purposes, so they aren't meant to be temporary
// These should be kept in sync with src/access.tsx if they are debug only.
export const URL_FLAGS = {
  CAPITAL_ENABLE_STANDARD_COMPOSITIONS_FOR_SYNTHETICS:
    'capital_enable_standard_compositions_for_synthetics' as const, // Enables standard Capital KYC compositions in Synthetics.
  SHOW_ARK_DEBUG_TOOLS: 'show_ark_debug_tools' as const, // Controls whether ARK's debugging tools are turned on for onboarding.
  ENABLE_UICONFIG_COPY_LINK: 'enable_uiconfig_copy_link' as const, // Enables copy link button in ui config component
  ENABLE_UICONFIG_THEME_PRESETS: 'enable_uiconfig_theme_presets' as const, // Enables theme presets dropdown in ui config component
  ENABLE_UICONFIG_DEBUG_THEMING_OPTIONS:
    'enable_uiconfig_debug_theming_options' as const, // Enables debug theming options (import JSON mode, copy link button, and extra theme presets in dropdown) in ui config component
  STRIPE_INTERNAL_OVERRIDE_FLAGS_IN_EXTERNAL_DOMAINS:
    'stripe_internal_override_flags_in_external_domains' as const, // Enables retrieving fresh account link on reload
  ENABLE_SUS_DUAL_REPORTING: 'enable_sus_dual_reporting' as const, // Enables double reporting for metrics/analytics, etc. (legacy vs new path)
  ENABLE_DEV_NOTICES: 'enable_dev_notices' as const, // Enables dev notices in the UI (used in connect-test and login as)
  ENABLE_EXTERNAL_ACCOUNT_COLLECTION_IN_FINANCIAL_ACCOUNTS:
    'enable_external_account_collection_in_financial_accounts' as const, // Forces external account collection in Financial Accounts.
  FORCE_REQUIRES_EXTERNAL_ACCOUNT_VERIFICATION:
    'force_requires_external_account_verification' as const, // forces the external account verification challenge
};

export const SERVER_CONTROLLED_FLAGS = {
  // Server side controlled behavior: these control whether some features work for certain account configurations
  ENABLE_PERSONALIZATION_DESIGN_SELECTION_ON_ISSUING_CARD_CREATION:
    'enable_personalization_design_selection_on_issuing_card_creation' as const,
  ISSUING_ALLOW_RETRIEVE_PHYSICAL_CARD_DETAILS:
    'issuing_allow_retrieve_physical_card_details' as const,
  ENABLE_ISSUING_API: 'enable_issuing_api' as const,
  ENABLE_TREASURY_API: 'enable_treasury_api' as const,
  ENABLE_TREASURY_TESTMODE: 'enable_treasury_testmode' as const,
  INSTANT_PAYOUTS_RTP_DISABLED: 'instant_payouts_rtp_disabled' as const, // disables Instant Payouts to US Banks via RTP and displays outage banner
  INSTANT_PAYOUTS_TO_DEBIT_CARDS_DISABLED:
    'instant_payouts_to_debit_cards_disabled' as const, // disables Instant Payouts to debit cards and displays outage banner
  CARD_PRESENT_BRING_YOUR_OWN_GATEWAY_PARAM_DOCS:
    'card_present_bring_your_own_gateway_param_docs' as const, // enables showing links to BYOG docs

  // Flags - these are temporary and are used for feature enablement
  ENABLE_TOPUPS: 'enable_topups' as const,
  EMBEDDED_V2_POM_CHALLENGES: 'embedded_v2_pom_challenges' as const,
  ENABLE_ADD_LINKED_ACCOUNTS: 'enable_add_linked_accounts' as const, // Enables the functionality to add linked accounts in account management
  ENABLE_DEBIT_CARD_ENTRY: 'enable_debit_card_entry' as const, // Enables user to select debit card as an external account
  // Read-switch: when enabled, EC reads the `smart_disputes_management` Account
  // Sessions feature instead of preserving the legacy hardcoded enabled behavior.
  ENABLE_SMART_DISPUTES_MANAGEMENT_FEATURE_CUTOVER:
    'enable_smart_disputes_management_feature_cutover' as const,
  ENABLE_UNIFIED_EVIDENCE_EXPERIENCE_EMBEDDED_COMPONENTS:
    'enable_unified_evidence_experience_embedded_components' as const,
  // Killswitch: when enabled for the connected merchant or Connect application,
  // disables explicit/manual Smart Disputes submission regardless of the
  // read-switch or the account-session feature.
  DISABLE_SMART_DISPUTES_MANAGEMENT_EMBEDDED_COMPONENTS:
    'disable_smart_disputes_management_embedded_components' as const,
  BANK_CONNECTIONS_IN_ONBOARDING_UNIFIED:
    'bank_connections_in_onboarding_unified' as const, // should we show bank connections in the external account composition in onboarding?
  PLATFORM_EXTERNAL_ACCOUNT_SETTINGS_ENABLED:
    'platform_external_account_settings_enabled' as const, // uses Platform External Account Settings (PEAS) for LEAF settings
  FINANCIAL_CONNECTIONS_ENABLE_CA_ACCOUNTS:
    'financial_connections_enable_ca_accounts' as const, // Enable Financial Connections in the Bank Account Verification flow, for Canadian bank account collection.
  CONNECT_EMBEDDED_MOBILE_SDK_GA: 'connect_embedded_mobile_sdk_ga' as const,
  ENABLE_MOBILE_COMPONENT_ANNOTATION_ENFORCEMENT:
    'enable_mobile_component_annotation_enforcement' as const,
  ENABLE_FINANCIAL_ACCOUNTS_MOBILE_VIEW:
    'enable_financial_accounts_mobile_view' as const, // enables mobile layout for the Financial Accounts embedded component
  ENABLE_PAYOUT_SCHEDULE_ONBOARDING_ARK_V2_EMBEDDED_FLAG_GLOBAL:
    'enable_payout_schedule_onboarding_ark_v2_embedded_flag_global' as const,
  ARKV2_DISABLE_DISABLE_SUBMIT_BUTTON_ON_FRONTEND_VALIDATION_ERRORS:
    'arkv2_disable_disable_submit_button_on_frontend_validation_errors' as const,
  ENABLE_EMBEDDED_DOCUMENTS_PAGINATION_TOTAL_COUNT:
    'enable_embedded_documents_pagination_total_count' as const, // enables API total count pagination in the Documents component
  ENABLE_EMBEDDED_METADATA_FILTER_IN_PAYMENTS_LIST:
    'enable_embedded_metadata_filter_in_payments_list' as const, // enables payment list items to be filtered by metadata
  ENABLE_METADATA_IN_PAYMENT_DETAILS:
    'enable_metadata_in_payment_details' as const, // enables metadata to be shown on payment details
  MANAGED_RISK_FOR_BANKS_BETA: 'managed_risk_for_banks_beta' as const, // denotes whether an account is part of the Stripe managed risk for banks configuration
  ENABLE_MANAGED_RISK_FOR_CUSTOM:
    'enable_stripe_owns_payments_losses_and_platform_owns_requirement_collection_accounts' as const,
  ENABLE_EMBEDDED_PAYOUT_DETAILS_TIMELINE:
    'enable_embedded_payout_details_timeline' as const, // enables payout details timeline component in embedded
  CONNECT_EMBEDDED_BALANCE_RESERVE_BREAKDOWN:
    'connect_embedded_balance_reserve_breakdown' as const,
  CONNECT_EMBEDDED_BALANCE_BACKEND_ARRIVAL_ESTIMATE:
    'connect_embedded_balance_backend_arrival_estimate' as const,
  ENABLE_FULL_EMBEDDED_REPORTING_DATA_AVAILABILITY:
    'enable_full_embedded_reporting_data_availability' as const,
  ENABLE_CONNECT_ONBOARDING_USE_API_EXTERNAL_ACCOUNT_COLLECTION:
    'enable_connect_onboarding_use_api_external_account_collection' as const,
  CAPITAL_OFFER_ALLOW_TINY_MINIMUM_ADVANCE_AMOUNT:
    'capital_offer_allow_tiny_minimum_advance_amount' as const,
  CAPITAL_ENABLE_LOC_SESSIONS_UI_IN_EMBEDDED:
    'capital_enable_loc_sessions_ui_in_embedded' as const,
  CAPITAL_ENABLE_NEW_APP_STATUS_TRACKER:
    'capital_enable_new_app_status_tracker' as const,
  CAPITAL_ENABLE_LINE_OF_CREDIT_EXPLAINER:
    'capital_enable_line_of_credit_explainer' as const,
  CAPITAL_ENABLE_SESSIONS_UI_ALL_FINANCINGS_IN_EMBEDDED:
    'capital_enable_sessions_ui_all_financings_in_embedded' as const,
  CAPITAL_ENABLE_SESSIONS_UI_ALL_FINANCINGS_IN_HOSTED:
    'capital_enable_sessions_ui_all_financings_in_hosted' as const,
  CAPITAL_ENABLE_SESSIONS_UI_PROMO_ALL_FINANCINGS_IN_EMBEDDED:
    'capital_enable_sessions_ui_promo_all_financings_in_embedded' as const,
  CAPITAL_ENABLE_SESSIONS_UI_PROMO_ALL_FINANCINGS_IN_HOSTED:
    'capital_enable_sessions_ui_promo_all_financings_in_hosted' as const,
  CAPITAL_ENABLE_PAYOUT_DESTINATION_CONSOLIDATION_AND_BALANCE_PROMO:
    'capital_enable_payout_destination_consolidation_and_balance_promo' as const,
  CAPITAL_FOR_PLATFORMS_ENABLE_APPLICATION_BACK_BUTTON_NAVIGATION:
    'capital_for_platforms_enable_application_back_button_navigation' as const,
  CAPITAL_FOR_PLATFORMS_ENABLE_IN_APP_DOWNGRADE_UX:
    'capital_for_platforms_enable_in_app_downgrade_ux' as const,
  CAPITAL_FOR_PLATFORMS_DOORDASH_APPLICATION_UI_CUSTOMIZATIONS:
    'capital_for_platforms_doordash_application_ui_customizations' as const,
  ENABLE_PAYOUT_TRANSACTIONS_EXPORT:
    'enable_payout_transactions_export' as const, // enables export button in payout transactions
  // Hide test data banners (and test data bank account)
  HIDE_TEST_DATA_BANNER: 'dashboard_hide_test_data_banner' as const,
  DISABLE_REQUIRES_PAYMENT_CAPABILITIES:
    'disable_requires_payment_capabilities' as const,
  DISABLE_ACCOUNT_MANAGEMENT_INJECTED_PERSON_ADDRESS_FIELDS:
    'disable_account_management_injected_person_address_fields' as const,
  RADAR_PRICING_CONNECTED_ACCOUNT_ONBOARDING_ENABLED:
    'radar_pricing_connected_account_onboarding_enabled' as const,
  ENABLE_TERMINAL_HARDWARE_EMBEDDED_COMPONENTS_GA:
    'enable_terminal_hardware_embedded_components_ga' as const,
  MMC_OUTBOUND_FLOWS_NETWORK_PARAMS:
    'mmc_outbound_flows_network_params' as const, // enables payout_method_options API path for NACHA payroll intent
  HRO_EMBEDDED_DUAL_METRICS_REPORTING_ENABLED:
    'hro_embedded_dual_metrics_reporting_enabled' as const,
  GLOBAL_PAYOUTS_HRO_CURRENCY_FROM_RECIPIENT_COUNTRY:
    'global_payouts_hro_currency_from_recipient_country' as const,
  ENABLE_EMBEDDED_SEND_RECEIPT: 'enable_embedded_send_receipt' as const, // enables the receipt module in the payment details component
  EFS_TRANSACTIONS_LIST_UNIFIED_BALANCE_TRANSACTIONS:
    'efs_transactions_list_unified_balance_transactions' as const,
};

export const FLAGS = {
  ...SERVER_CONTROLLED_FLAGS,
  ...URL_FLAGS,
  ...ComponentOverrideFlag,
};

// This type exists to ensure that FLAGS has only flags as values
export const doNotImportEnsureObjectOnlyContainsFlags: Record<string, Flag> =
  FLAGS;
